<template>
  <div class="stability-card">
    <div class="stability-card__legend">
      <div
          v-for="item in normalizedItems"
          :key="item.key"
          class="stability-card__legend-row"
      >
        <div class="stability-card__legend-name">
          <span
              class="stability-card__mark"
              :style="{ backgroundColor: item.color }"
          ></span>

          <span>{{ item.label }}</span>

          <a-popover
              v-if="item.warning"
              trigger="hover"
              placement="top"
          >
            <template #content>
              <div class="stability-card__popover">
                {{ item.warning }}
              </div>
            </template>

            <InfoCircleOutlined class="stability-card__warning-icon" />
          </a-popover>
        </div>

        <span
            class="stability-card__change"
            :class="{
              'positive-change': item.change < 0,
              'negative-change': item.change > 0,
              'neutral-change': item.change === 0
            }"
        >
          {{ getChangeText(item.change) }}
        </span>

        <span class="stability-card__value">
          {{ item.value }}
        </span>
      </div>
    </div>

    <div class="stability-card__chart">
      <canvas ref="chartCanvas"></canvas>
    </div>
  </div>
</template>

<script>
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
} from 'chart.js'
import { InfoCircleOutlined } from '@ant-design/icons-vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

Chart.register(
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Tooltip,
)

export default {
  name: 'StabilityMotivationIndexCard',

  components: {
    InfoCircleOutlined,
  },

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    chartLabels: {
      type: Array,
      default: () => [],
    },
    chartDatasets: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const chartCanvas = ref(null)
    let chartInstance = null

    const toNumber = (value) => {
      const number = Number(value)

      return Number.isFinite(number) ? number : 0
    }

    const normalizedItems = computed(() => {
      return props.items.map((item) => ({
        ...item,
        change: toNumber(item.change),
        value: toNumber(item.value),
      }))
    })

    const getChangeText = (value) => {
      if (value > 0) {
        return `↑ ${value}`
      }

      if (value < 0) {
        return `↓ ${Math.abs(value)}`
      }

      return '–'
    }

    const destroyChart = () => {
      if (chartInstance) {
        chartInstance.destroy()
        chartInstance = null
      }
    }

    const getDatasets = () => {
      return props.chartDatasets.map((item) => ({
        label: item.label,
        data: item.values,
        borderColor: item.color,
        backgroundColor: item.color,
        borderWidth: 1,
        borderDash: item.key === 'fatigue' ? [] : [5, 5],
        pointRadius: 0,
        pointHoverRadius: 3,
        fill: false,
        tension: 0,
      }))
    }

    const renderChart = async () => {
      await nextTick()

      if (!chartCanvas.value) {
        return
      }

      destroyChart()

      const ctx = chartCanvas.value.getContext('2d')

      chartInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels: props.chartLabels,
          datasets: getDatasets(),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: true,
              displayColors: true,
            },
            datalabels: {
              display: false,
            },
          },
          scales: {
            x: {
              grid: {
                color: '#EEF0F3',
                drawTicks: false,
              },
              border: {
                display: false,
              },
              ticks: {
                color: 'rgba(0, 0, 0, 0.45)',
                font: {
                  size: 12,
                },
              },
            },
            y: {
              min: -10,
              max: 10,
              ticks: {
                stepSize: 5,
                color: 'rgba(0, 0, 0, 0.45)',
                font: {
                  size: 12,
                },
              },
              grid: {
                color: '#EEF0F3',
                drawTicks: false,
              },
              border: {
                display: false,
              },
            },
          },
        },
      })
    }

    onMounted(() => {
      renderChart()
    })

    onBeforeUnmount(() => {
      destroyChart()
    })

    watch(
        () => [props.chartLabels, props.chartDatasets],
        () => {
          renderChart()
        },
        {
          deep: true,
        }
    )

    return {
      chartCanvas,
      normalizedItems,
      getChangeText,
    }
  },
}
</script>

<style scoped lang="scss">
.stability-card {
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: start;
}

.stability-card__legend {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 34px;
}

.stability-card__legend-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 48px 42px;
  align-items: center;
  column-gap: 12px;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.stability-card__legend-name {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.stability-card__legend-name span:last-of-type {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stability-card__mark {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex: 0 0 auto;
}

.stability-card__warning-icon {
  color: #FAAD14;
  font-size: 13px;
  flex: 0 0 auto;
  cursor: pointer;
}

.stability-card__popover {
  max-width: 240px;
  font-size: 13px;
  line-height: 130%;
}

.stability-card__change {
  text-align: right;
  white-space: nowrap;
}

.stability-card__value {
  text-align: right;
  color: rgba(0, 0, 0, 0.88);
  white-space: nowrap;
}

.stability-card__chart {
  width: 100%;
  height: 150px;
  margin-top: auto;
}

.positive-change {
  color: var(--main-green);
}

.negative-change {
  color: #F55565;
}

.neutral-change {
  color: rgba(0, 0, 0, 0.45);
}
</style>